<script lang="ts">
  /**
   * Posts & Comments — the dashboard's wiring around `@ui`'s `<InboxDeck>`.
   *
   * The queue, the grouping into posts, the focus that walks a post's unanswered
   * rows, Mark as read and its undo are all the shared component's, identical to the
   * Helper panel's. THREE THINGS ARE THIS SURFACE'S OWN:
   *
   *   * The reply is QUEUED for a Helper to send, not posted here — no browser on
   *     this page is signed into Skool.
   *   * A 401 navigates to the login rather than rendering a message.
   *   * The sidebar badges, which the deck hands back already recomputed.
   *
   * A LIST AND A READING DRAWER USED TO LIVE HERE. Both are gone: the deck shows one
   * post at a time with its thread already open and the reply box on the row that
   * needs it, so there is nothing to select and nothing to open.
   */
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import OutboxQueue from '../lib/ui/OutboxQueue.svelte';
  import HelperPresence from '../lib/ui/HelperPresence.svelte';
  import { askHelperToSend } from '../lib/helper.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import InboxDeck from '@ui/InboxDeck.svelte';
  import ReplyComposer from '@ui/composer/ReplyComposer.svelte';
  import type { MentionCandidate } from '@ui/composer/mentions';
  import type { ThreadTarget } from '@ui/PostThread.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    type AdminUser,
    type PostDetail,
  } from '../lib/admin';
  import { community, withCommunity } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchQueue, queueLike, queueReply, type QueuePage } from '@api';
  import { applyBadges } from '../lib/todos.svelte';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  /** Null until the first page says. `false` is the only value that gates the screen —
   *  "we do not know yet" must not read as "you have no moderators". */
  let hasModerators = $state<boolean | null>(null);
  let deck = $state<ReturnType<typeof InboxDeck> | null>(null);

  const LIMIT = 25;

  let nowMs = $state(Date.now());
  $effect(() => {
    const t = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(t);
  });

  // Two screens on one page, mirroring the Helper extension: the moderation inbox
  // and the Outbox Queue (what your Helper is about to send + what's already gone).
  // `?tab=outbox` deep-links straight to the queue (old /moderation/queue links).
  let screen = $state<'inbox' | 'outbox'>(
    new URLSearchParams(window.location.search).get('tab') === 'outbox' ? 'outbox' : 'inbox',
  );
  function pickScreen(next: 'inbox' | 'outbox'): void {
    if (screen === next) return;
    screen = next;
    const url = new URL(window.location.href);
    if (next === 'outbox') url.searchParams.set('tab', 'outbox');
    else url.searchParams.delete('tab');
    history.replaceState(history.state, '', url);
  }

  // Nudge the Outbox tab to re-fetch after we queue a reply, so its tallies and list
  // reflect the change the moment the moderator switches over to watch it land.
  let queueRefresh = $state(0);
  const bumpQueue = () => (queueRefresh += 1);

  /**
   * The account and the first page of the inbox IN THE SAME ROUND TRIP (doc 04
   * §4.1). `withCommunity` issues the inbox fetch immediately against the community
   * id already in localStorage; if that id is stale the server says so and it
   * retries once against the `selected_skool_id` the bootstrap resolved — which has
   * already arrived, because it went out alongside. 3 requests / 3 RTT → 2 / 1.
   *
   * The page is handed to the deck as its `seed` rather than dropped, which is the
   * only reason this is still worth doing here: the deck would otherwise fetch the
   * same first page itself, one round trip later.
   */
  let seed = $state<{ skoolId: number; bound: string; page: QueuePage } | null>(null);

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      let fetchedFor: number | null = null;
      const [boot, page] = await Promise.all([
        ensureBootstrap(),
        withCommunity((id) => {
          fetchedFor = id;
          // The deck's OPENING WINDOW and page size — see `@ui/InboxDeck`'s `WINDOWS`.
          // Any difference and the seed would not describe what it is about to show,
          // which is why the bound travels with it and the deck checks both. `counts`
          // holds that one bound and no other: it is the progress bar's denominator,
          // and a wider entry would widen the scan behind it for nothing.
          return fetchQueue(id, { last: '1d' }, { limit: LIMIT, counts: ['1d'] });
        }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      if (fetchedFor != null) seed = { skoolId: fetchedFor, bound: '1d', page };
      // `has_moderators` rides along on the queue page (#171), so nothing here has to
      // ask a second time — the deck hands it back through `onReady`.
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  /**
   * Whoever wrote the row being answered, as a mention candidate.
   *
   * A reply in a Skool thread almost always opens by naming the person it answers, and
   * the thread is already loaded — so the box opens that way rather than making the
   * moderator type a name the screen is already showing.
   */
  function addresseeOf(detail: PostDetail, to: ThreadTarget): MentionCandidate | null {
    const a =
      to.kind === 'post'
        ? detail.post.author
        : (detail.comments.find((c) => c.id === to.id)?.author ?? null);
    if (!a?.external_id) return null;
    return {
      externalId: a.external_id,
      name: a.name,
      handle: a.handle,
      pictureUrl: a.picture_profile,
    };
  }

  /**
   * Queue the like, and let the deck treat it as the answer.
   *
   * THE SAME PIPELINE AS A REPLY, deliberately: this page has no Skool session, and the
   * one answer to that here is the outbox. A Helper drains it — instantly when one is in
   * this browser, on its next wake otherwise — and either way the row survives a closed
   * tab, retries, and does not care which machine the Helper is on.
   *
   * The drain is kicked but NOT awaited. Queueing is the commitment and the only thing
   * that can honestly fail here; the kick is an accelerator, and blocking the thumb on a
   * Skool round trip would trade the durability we just bought for the latency we were
   * trying to avoid.
   */
  async function like(to: {
    kind: 'post' | 'comment';
    id: number;
    externalId: string;
  }): Promise<void> {
    const id = community.selectedId;
    if (id == null) return;
    try {
      await queueLike(id, to.kind, to.id);
      bumpQueue(); // a queued like is an outbox row — refresh the sidebar
      void askHelperToSend();
      deck?.liked(to);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      throw err;
    }
  }

  /** This app navigates away on a 401 rather than rendering anything. */
  function explain(err: unknown): string | null {
    if (handle401(err, navigate)) return null;
    return err instanceof Error ? err.message : 'Failed to load the inbox.';
  }

  /**
   * THE SEND PATH. This is what actually puts a reply in front of the Helper to
   * post to Skool, so the two properties that matter are stated rather than assumed:
   *
   *  * **It cannot double-post.** `POST …/outbox/items` is an UPSERT over the same
   *    partial unique index the legacy route used (`uq_outbox_pending_subject`, one
   *    non-terminal row per (community, owner, target)), and it refuses with 409
   *    `reply_in_flight` when that row is already `leased` — in the Helper's hands
   *    and possibly on Skool already. Submitting again REPLACES the pending row and
   *    re-queues it with a fresh idempotency key, which is why nothing here has to
   *    reason about whether a reply is already waiting. All of that is one
   *    `domain.outbox.enqueue_reply` that both surfaces call, so the guard cannot
   *    drift between them.
   *  * **A refusal is not swallowed.** Re-thrown so `<ReplyComposer>` shows it
   *    instead of ticking "Queued ✓" over a reply that was never queued.
   */
  async function queue(
    to: { kind: 'post' | 'comment'; id: number },
    text: string,
  ): Promise<string | void> {
    const id = community.selectedId;
    if (id == null) return;
    try {
      await queueReply(id, to.kind, to.id, text);
      bumpQueue(); // a queued reply is an outbox row — refresh the sidebar
      return await askHelperToSend();
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      throw err;
    }
  }
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The eyebrow said "Moderation" — a name for this page that exists nowhere in
           the navigation. It lives under Mission Control, and that is what it says now. -->
      <PageHeader section="Mission Control" title="Posts &amp; Comments" />
      <!-- Two screens on one page, same idea as the Helper extension: the inbox, and
           the Outbox Queue. A raised segmented control so it reads as a control rather
           than melting into the grey canvas — left-aligned with the heading above it,
           because a centred control on a full-width page floats away from everything
           it belongs to. -->
      <div class="flex">
        <div role="tablist" class="inline-flex gap-1 rounded-xl border border-base-300 bg-base-100 p-1 shadow-sm">
          <button
            role="tab"
            aria-selected={screen === 'inbox'}
            class="rounded-lg px-4 py-1.5 text-sm font-medium transition-colors
              {screen === 'inbox'
                ? 'bg-primary text-primary-content shadow-sm'
                : 'text-base-content/70 hover:bg-base-200'}"
            onclick={() => pickScreen('inbox')}
          >Posts &amp; Comments</button>
          <button
            role="tab"
            aria-selected={screen === 'outbox'}
            class="rounded-lg px-4 py-1.5 text-sm font-medium transition-colors
              {screen === 'outbox'
                ? 'bg-primary text-primary-content shadow-sm'
                : 'text-base-content/70 hover:bg-base-200'}"
            onclick={() => pickScreen('outbox')}
          >Outbox Queue</button>
        </div>
      </div>

      <!-- Shared Helper presence — sits under the tab selector, both tabs. -->
      <div class="mt-3 flex">
        <HelperPresence />
      </div>

      {#if screen === 'outbox'}
        <OutboxQueue skoolId={community.selectedId} refresh={queueRefresh} />
      {:else if hasModerators === false}
        <div class="mt-6 rounded-box border hair bg-base-100 p-8 text-center">
          <h2 class="font-display text-lg font-semibold">Flag your moderators first</h2>
          <p class="mx-auto mt-1.5 max-w-sm text-sm text-base-content/72">
            The inbox tracks what your team hasn't answered yet. Mark who's on your team in
            <button class="link link-primary" onclick={() => navigate('/admin/preferences')}>Preferences</button>.
          </p>
        </div>
      {:else}
        <InboxDeck
          bind:this={deck}
          skoolId={community.selectedId}
          {seed}
          now={nowMs}
          onLike={like}
    errorText={explain}
          onReady={({ hasModerators: h }) => (hasModerators = h)}
          onChanged={({ badges }) => {
            if (badges) applyBadges(community.selectedId ?? 0, badges);
            bumpQueue();
          }}
          {composer}
        />
      {/if}
    </PageContainer>
  {/if}
</AdminShell>

{#snippet deckControls()}
  <!-- MOVING THROUGH THE QUEUE IS ONE CONTROL, joined: back a step, or on to the next.
       They were two loose buttons floating left of the send group and read as unrelated
       to it and to each other. Sending stays separate, at the far right, because it is
       a different kind of act. -->
  <div class="join">
    <button
      class="btn btn-outline btn-sm join-item gap-1.5 px-2.5 font-normal text-base-content/70"
      onclick={() => void deck?.back()}
      disabled={!deck?.canUndo()}
      title="Undo the last one"
      aria-label="Undo the last one"
    >
      <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14 4 9l5-5" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></svg>
      Back
    </button>
    <button
      class="btn btn-outline btn-sm join-item gap-1.5"
      onclick={() => void deck?.markRead()}
      title="Nothing to answer here — mark it read and move on"
    >
      Skip
      <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
    </button>
  </div>
{/snippet}

{#snippet composer(detail: PostDetail, replyingTo: ThreadTarget)}
  <!-- Keyed on the row being answered, so walking down a post's unanswered comments
       always starts with an empty box rather than half a sentence aimed at the last
       one. -->
  {#key `${replyingTo.kind}:${replyingTo.id}`}
    <ReplyComposer
      skoolId={community.selectedId}
      kind={replyingTo.kind}
      targetId={replyingTo.id}
      addressee={addresseeOf(detail, replyingTo)}
      submitLabel="Reply"
      jumpWhenOffscreen
      doneLabel="Queued"
      leading={deckControls}
      onSubmit={async (text) => {
        await queue(replyingTo, text);
        // Answered — walk on to the next thing waiting on this post, or the next post.
        deck?.replied(replyingTo);
      }}
    />
  {/key}
{/snippet}
